<template>
  <div class="ca-acct-root">
    <div v-if="hasAccounts">

      <!-- Header: account selector + actions -->
      <div class="ca-acct-header">
        <button id="account-button-event" class="ca-acct-selector" :class="{ demo: selectedAccount.isDemo }">
          <span class="ca-acct-selector-icon"><i class="fa-solid fa-wallet"></i></span>
          <span class="ca-acct-selector-info">
            <span class="ca-acct-id-label">{{ $t('columns.account') }}</span>
            <span class="ca-acct-id-value">
              #{{ selectedAccount.id }}
              <span v-if="selectedAccount.isDemo" class="ca-acct-demo">DEMO</span>
              <span v-else class="ca-acct-real">REAL</span>
            </span>
          </span>
          <i class="fa-solid fa-chevron-down ca-acct-chevron"></i>
        </button>

        <div class="ca-acct-actions">
          <button class="ca-acct-action" :class="selectedAccount.active ? 'on-success' : 'on-danger'"
                  @click="accountActiveToggle">
            <i class="fa-solid" :class="{'fa-circle-check': selectedAccount.active, 'fa-circle-xmark': !selectedAccount.active}"></i>
            <span>{{ selectedAccount.active ? $t('columns.active') : $t('columns.inactive') }}</span>
          </button>
          <button class="ca-acct-action" :class="{ 'on-danger': selectedAccount.restrictTrades }"
                  v-b-tooltip.hover :title="$t('account.restrictTrades.tooltip')"
                  @click="restrictTradesToggle">
            <i class="fa-solid" :class="selectedAccount.restrictTrades ? 'fa-lock' : 'fa-lock-open'"></i>
            <span>{{ selectedAccount.restrictTrades ? $t('account.restrictTrades.restricted') : $t('account.restrictTrades.allowed') }}</span>
          </button>
          <button v-if="hasPermission(permissions.ACCOUNT_DELETE)" class="ca-acct-action ca-acct-action-delete"
                  :title="$t('account.delete.action')" @click="openDeleteAccountModal">
            <i class="fa-solid fa-trash-can"></i>
            <span>{{ $t('account.delete.action') }}</span>
          </button>
          <button class="ca-acct-action" @click="$refs.accountSyncModal.showModal()">
            <i class="fa-solid fa-rotate"></i>
            <span>{{ $t('columns.sync') }}</span>
          </button>
          <button class="ca-acct-action" @click="$refs.tradeReportModal.show()">
            <i class="fa-solid fa-file-invoice"></i>
            <span>{{ $t('account.report.action') }}</span>
          </button>
          <button v-if="selectedAccount.id !== '0'" class="ca-acct-action ca-acct-action-trade" @click="openNewTrade">
            <i class="fa-solid fa-plus"></i>
            <span>{{ $t('trade.newTrade') }}</span>
          </button>
        </div>
      </div>

      <!-- Stat tiles -->
      <div class="ca-acct-stats">
        <div class="ca-acct-stat">
          <div class="ca-acct-stat-label">{{ $t('columns.currency') || 'Currency' }}</div>
          <div class="ca-acct-stat-value ca-acct-stat-text">{{ selectedAccount.currency || '—' }}</div>
        </div>
        <div class="ca-acct-stat">
          <div class="ca-acct-stat-label">Leverage</div>
          <div class="ca-acct-stat-value ca-acct-stat-text">1:{{ selectedAccount.defaultLeverage || '200' }}</div>
        </div>
        <div class="ca-acct-stat ca-acct-stat-money">
          <div class="ca-acct-stat-label"><i class="fa-solid fa-coins ca-acct-stat-icon ca-acct-icon-warn"></i> {{ $t('columns.balance') }}</div>
          <div class="ca-acct-stat-value">{{ (selectedAccount.balance || 0) | numberFormatter }}</div>
        </div>
        <div class="ca-acct-stat ca-acct-stat-money">
          <div class="ca-acct-stat-label"><i class="fa-solid fa-credit-card ca-acct-stat-icon ca-acct-icon-info"></i> {{ $t('columns.credit') }}</div>
          <div class="ca-acct-stat-value">{{ (selectedAccount.credit || 0) | numberFormatter }}</div>
        </div>
        <div v-if="selectedAccount.margin > 0" class="ca-acct-stat ca-acct-stat-money">
          <div class="ca-acct-stat-label"><i class="fa-solid fa-chart-pie ca-acct-stat-icon"></i> Margin</div>
          <div class="ca-acct-stat-value">
            {{ (selectedAccount.margin || 0).toFixed(2) }} {{ $t(`currencies.${selectedAccount.currency}.symbol`) }}
            <span v-if="selectedAccount.marginFreePercentage <= 1500" class="ca-acct-stat-sub">({{ selectedAccount.marginFreePercentage }}%)</span>
            <span v-if="selectedAccount.marginCall" class="ca-acct-margin-call">CALL</span>
          </div>
        </div>
        <div v-if="selectedAccount.margin > 0" class="ca-acct-stat ca-acct-stat-money">
          <div class="ca-acct-stat-label"><i class="fa-solid fa-scale-balanced ca-acct-stat-icon"></i> Equity</div>
          <div class="ca-acct-stat-value">{{ (selectedAccount.equity || 0) | numberFormatter }}</div>
        </div>
        <div v-if="selectedAccount.profit != null" class="ca-acct-stat ca-acct-stat-money">
          <div class="ca-acct-stat-label"><i class="fa-solid fa-arrow-trend-up ca-acct-stat-icon"></i> Profit</div>
          <div class="ca-acct-stat-value"
               :class="{'text-danger': selectedAccount.profit < 0, 'text-success': selectedAccount.profit > 0}">
            {{ selectedAccount.profit | numberFormatter }} {{ $t(`currencies.${selectedAccount.currency}.symbol`) }}
          </div>
        </div>
        <div v-else-if="selectedAccount.totalOpenTrades > 0" class="ca-acct-stat ca-acct-stat-money">
          <div class="ca-acct-stat-label"><i class="fa-solid fa-arrow-trend-up ca-acct-stat-icon"></i> Profit</div>
          <div class="ca-acct-stat-value"><b-spinner small/></div>
        </div>
      </div>

      <trade-account-details ref="trade-account-details" v-if="selectedAccount.id !== '0'" v-model="selectedAccount" />
    </div>

    <div v-else-if="!loading" class="ca-acct-empty">
      <div class="ca-acct-empty-icon"><i class="fa-sharp fa-solid fa-wallet"></i></div>
      <div class="ca-acct-empty-text">{{ $t('account.noAccounts') }}</div>
      <b-button variant="primary" class="mt-3" @click="newAccount">
        <i class="fa-sharp fa-solid fa-plus mr-1"></i>{{ $t('account.newAccount') }}
      </b-button>
    </div>

    <b-popover v-if="hasAccounts" ref="popover" custom-class="account-list-popover" placement="bottom" target="account-button-event">
      <b-list-group class="m-0 p-0">
        <b-list-group-item class="cursor-pointer" v-for="acc in accounts" v-bind:key="acc.id" @click="onAccountSelect(acc.id)">
          #{{acc.id}} <span class="ca-acct-pill" :class="acc.isDemo ? 'ca-acct-pill-demo' : 'ca-acct-pill-real'">{{ acc.isDemo ? 'DEMO' : 'REAL' }}</span>
        </b-list-group-item>
        <b-list-group-item class="cursor-pointer ca-acct-new-row" @click="newAccount">
          <i class="fa-sharp fa-solid fa-plus mr-1"></i>{{$t('account.newAccount')}}
        </b-list-group-item>
      </b-list-group>
    </b-popover>
    <new-account-modal ref="account-modal" @created="onAccountCreated"/>
    <account-sync-modal ref="accountSyncModal" v-bind:account-id="selectedAccountId" v-bind:account-currency="selectedAccountCurrency" />
    <account-trade-report-modal ref="tradeReportModal" v-bind:account-id="selectedAccountId"
                                v-bind:currency="selectedAccountCurrency" v-bind:client="client"/>

    <b-modal ref="deleteAccountModal" centered hide-header hide-footer modal-class="ca-del-modal">
      <div class="ca-del-header">
        <div class="ca-del-icon"><i class="fa-solid fa-triangle-exclamation"></i></div>
        <div class="ca-del-title">{{ $t('account.delete.title', { id: selectedAccount.id }) }}</div>
        <div class="ca-del-text">{{ $t('account.delete.description') }}</div>
      </div>

      <b-alert :show="!canDeleteSelectedAccount" variant="danger" class="ca-del-guard">
        <div class="ca-del-guard-title">
          <i class="fa-solid fa-ban mr-1"></i>{{ $t('account.delete.blockedTitle') }}
        </div>
        <ul class="ca-del-guard-list">
          <li v-if="selectedAccount.totalOpenTrades > 0">
            {{ $t('account.delete.blockedOpenTrades', { count: selectedAccount.totalOpenTrades }) }}
          </li>
          <li v-if="(selectedAccount.balance || 0) !== 0">
            {{ $t('account.delete.blockedBalance', { balance: selectedAccount.balance }) }}
          </li>
          <li v-if="(selectedAccount.credit || 0) !== 0">
            {{ $t('account.delete.blockedCredit', { credit: selectedAccount.credit }) }}
          </li>
        </ul>
      </b-alert>

      <div class="ca-del-actions">
        <b-button variant="outline-secondary" :disabled="deletingAccount" @click="$refs.deleteAccountModal.hide()">
          {{ $t('operations.cancel') }}
        </b-button>
        <b-button variant="danger" :disabled="!canDeleteSelectedAccount || deletingAccount" @click="confirmDeleteAccount">
          <b-spinner v-if="deletingAccount" small class="mr-1"/>
          <i v-else class="fa-solid fa-trash-can mr-1"></i>{{ $t('account.delete.confirm') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import TradeAccountDetails from '@/components/partial/accounts/TradeAccountDetails'
import NewAccountModal from '@/components/modal/newAccountModal.vue'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinPopover from '@/components/mixin/mixinPopover'
import mixinPermissions from '@/components/mixin/mixinPermissions'
import account from '@/store/system/account'
import AccountSyncModal from '@/components/modal/AccountSyncModal.vue'
import AccountTradeReportModal from '@/components/modal/AccountTradeReportModal.vue'

export default {
  name: 'ClientAccounts',
  mixins: [mixinBase, mixinNotifications, mixinPopover, mixinPermissions],
  components: { AccountSyncModal, AccountTradeReportModal, NewAccountModal, TradeAccountDetails },
  data: () => ({
    loading: true,
    error: null,
    accounts: [],
    deletingAccount: false,
    selectedAccount: { id: '0', active:false }
  }),
  props: {
    clientId: String,
    organizationId: String,
    client: {
      type: Object,
      default: null
    }
  },
  watch: {
    clientId: {
      handler() {
        this.refresh()
      },
      immediate: true
    }
  },
  created() {
    this.$root.$on('system::account::update', this.onAccountUpdate)
  },
  beforeDestroy() {
    this.$root.$off('system::account::update', this.onAccountUpdate)
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    hasAccounts(){
      return Array.isArray(this.accounts) && this.accounts.length > 0;
    },
    account() {
      return account
    },
    selectedAccountId(){
      return this.selectedAccount?.id || null;
    },
    selectedAccountCurrency(){
      return this.selectedAccount?.currency || 'EUR';
    },
    canDeleteSelectedAccount() {
      return this.selectedAccount != null
          && !(this.selectedAccount.totalOpenTrades > 0)
          && (this.selectedAccount.balance || 0) === 0
          && (this.selectedAccount.credit || 0) === 0;
    }
  },
  methods: {
    ...mapActions('account', ['getAccountsByClient', 'setActive', 'setRestrictTrades', 'deleteAccount']),
    async refresh() {
      try {
        this.loading = true;
        this.accounts = await this.getAccountsByClient(this.clientId);
        this.setDefaultSelectedAccount()

        if (this.$refs['trade-account-details']) {
          this.$refs['trade-account-details'].refresh();
        }
      } catch (e) {
        this.error = e
      } finally {
        this.loading = false;
      }
    },
    setDefaultSelectedAccount(){
      if(this.selectedAccount != null && this.selectedAccount.id !== '0') {
        let acc = this.accounts.find(x => x.id === this.selectedAccount.id);
        if(acc != null) {
          this.selectedAccount = { profit: null, ...acc};
          return;
        }
      }
      if (Array.isArray(this.accounts) && this.accounts.length > 0) {
        this.selectedAccount = { profit: null, ...this.accounts[0] };
      } else {
        this.selectedAccount = { profit: null, id: '0' }
      }
    },
    onAccountSelect(id) {
      try {
        if (this.selectedAccount.id === id) {
          return
        }
        this.selectedAccount = this.accounts.find(x => x.id === id)
      } finally {
        this.closeAllPopovers();
      }
    },
    onAccountUpdate(account) {
      console.log('onAccountUpdate', account)
      const index = this.accounts.findIndex(x => x.id === account.id);
      if (index !== -1) {
        this.accounts[index] = account;
      }
      if (this.selectedAccount.id === account.id) {
        this.selectedAccount = account;
      }
    },
    newAccount() {
      this.$refs['account-modal'].showModal(this.clientId, this.organizationId)
    },
    onAccountCreated(account) {
      if(!Array.isArray(this.accounts)) {
        this.accounts = []
      }
      this.accounts.push(account)
      this.selectedAccount = account
    },
    accountActiveToggle(){
      this.setActive({account: this.selectedAccount.id, active: !this.selectedAccount.active})
          .then(this.refresh)
    },
    restrictTradesToggle(){
      const target = !this.selectedAccount.restrictTrades;
      this.setRestrictTrades({account: this.selectedAccount.id, restrictTrades: target})
          .then(() => {
            // Reflect immediately — refresh() below re-syncs from the server.
            this.selectedAccount = { ...this.selectedAccount, restrictTrades: target };
            this.showSuccessNotification(this.$t(target ? 'account.restrictTrades.restricted' : 'account.restrictTrades.allowed'));
            return this.refresh();
          })
          .catch(e => {
            this.showErrorNotification(this.$t('globalMessages.updateDataError', { system: this.$t('columns.account') }),
                this.getErrorDescription(e.response ? e.response.data : e), null, e);
          });
    },
    openDeleteAccountModal() {
      this.$refs.deleteAccountModal.show()
    },
    // Opens the new-trade order modal owned by the account-details child.
    openNewTrade() {
      const details = this.$refs['trade-account-details'];
      if (details && typeof details.newTradeRequest === 'function') {
        details.newTradeRequest();
      }
    },
    async confirmDeleteAccount() {
      if (!this.canDeleteSelectedAccount || this.deletingAccount) {
        return
      }
      const accountId = this.selectedAccount.id
      try {
        this.deletingAccount = true
        await this.deleteAccount(accountId)
        this.showSuccessNotification(this.$t('account.delete.successTitle'), this.$t('account.delete.successMessage', { id: accountId }))
        this.$refs.deleteAccountModal.hide()
        await this.refresh()
      } catch (e) {
        this.showErrorNotification(this.$t('account.delete.errorTitle'), this.getErrorDescription(e.response ? e.response.data : e), null, e)
      } finally {
        this.deletingAccount = false
      }
    },

  }
}
</script>

<style lang="scss">
.account-list-popover {
  .popover-body { padding: 0; }
  .list-group-item {
    border-radius: 0;
    border-left: none;
    border-right: none;
    font-weight: 500;
    transition: background 0.15s ease, color 0.15s ease;
    &:first-child { border-top: none; }
    &:hover { background: #eff6ff; color: #2563eb; }
  }
  .ca-acct-new-row {
    color: #2563eb;
    font-weight: 600;
    border-top: 2px solid #e2e8f0;
  }
  .ca-acct-pill {
    margin-left: 6px;
    font-size: 0.62rem;
    font-weight: 700;
    padding: 1px 7px;
    border-radius: 999px;
    letter-spacing: 0.5px;
  }
  .ca-acct-pill-demo { background: #fef3c7; color: #92400e; }
  .ca-acct-pill-real { background: #dcfce7; color: #166534; }
}

.ca-del-modal {
  .modal-body { padding: 24px; }
  .ca-del-header { text-align: center; margin-bottom: 16px; }
  .ca-del-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 12px;
    border-radius: 50%;
    background: #fef2f2;
    color: #dc2626;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.7rem;
  }
  .ca-del-title {
    font-size: 1.15rem;
    font-weight: 800;
    color: #dc2626;
    margin-bottom: 6px;
  }
  .ca-del-text { font-size: 0.87rem; color: #475569; }
  .ca-del-guard {
    font-size: 0.84rem;
    margin-bottom: 0;
  }
  .ca-del-guard-title { font-weight: 700; margin-bottom: 6px; }
  .ca-del-guard-list {
    margin: 0;
    padding-left: 18px;
    li { margin-bottom: 4px; }
  }
  .ca-del-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 18px;
  }
}
.dark-layout .ca-del-modal {
  .ca-del-icon { background: rgba(239, 68, 68, 0.15); color: #ff8a95; }
  .ca-del-title { color: #ff8a95; }
  .ca-del-text { color: #cbd5e1; }
}
</style>

<style scoped>
.ca-acct-root {
  --acct-text: #0f172a;
  --acct-text-2: #475569;
  --acct-text-muted: #94a3b8;
  --acct-bg: #ffffff;
  --acct-surface: #f8fafc;
  --acct-border: #e2e8f0;
  --acct-border-strong: #cbd5e1;
  --acct-primary: #2563eb;
  --acct-primary-soft: #eff6ff;
  --acct-success: #16a34a;
  --acct-success-soft: #f0fdf4;
  --acct-warn: #f59e0b;
  --acct-warn-soft: #fffbeb;
  --acct-danger: #dc2626;
  --acct-danger-soft: #fef2f2;
  --acct-info: #0284c7;
  --acct-info-soft: #f0f9ff;

  color: var(--acct-text);
}

.dark-layout .ca-acct-root {
  --acct-text: #f1f5f9;
  --acct-text-2: #cbd5e1;
  --acct-text-muted: #94a3b8;
  --acct-bg: #2a2f3a;
  --acct-surface: #1e293b;
  --acct-border: #475569;
  --acct-border-strong: #64748b;
  --acct-primary: #60a5fa;
  --acct-primary-soft: rgba(96, 165, 250, 0.15);
}

/* ===== HEADER ===== */
.ca-acct-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.ca-acct-selector {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: var(--acct-bg);
  border: 1px solid var(--acct-border);
  border-radius: 12px;
  padding: 10px 16px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  text-align: left;
}
.ca-acct-selector:hover {
  border-color: var(--acct-primary);
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.12);
}

.ca-acct-selector-icon {
  width: 38px; height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #60a5fa 0%, #2563eb 100%);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);
}

.ca-acct-selector.demo .ca-acct-selector-icon {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.25);
}

.ca-acct-selector-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.ca-acct-id-label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--acct-text-muted);
}
.ca-acct-id-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--acct-text);
  display: flex;
  align-items: center;
  gap: 8px;
}
.ca-acct-demo {
  font-size: 0.6rem;
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  letter-spacing: 0.6px;
}
.ca-acct-real {
  font-size: 0.6rem;
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  letter-spacing: 0.6px;
}
.dark-layout .ca-acct-demo { background: rgba(251, 191, 36, 0.18); color: #fcd34d; }
.dark-layout .ca-acct-real { background: rgba(34, 197, 94, 0.18); color: #6dd29a; }

.ca-acct-chevron {
  color: var(--acct-text-muted);
  font-size: 0.7rem;
  margin-left: 6px;
}

.ca-acct-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.ca-acct-action {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--acct-bg);
  border: 1px solid var(--acct-border);
  border-radius: 10px;
  padding: 8px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--acct-text);
  cursor: pointer;
  transition: all 0.15s ease;
}
.ca-acct-action:hover {
  border-color: var(--acct-primary);
  color: var(--acct-primary);
  background: var(--acct-primary-soft);
}
.ca-acct-action.on-success {
  background: var(--acct-success-soft);
  border-color: rgba(22, 163, 74, 0.3);
  color: var(--acct-success);
}
.ca-acct-action.on-success:hover {
  background: var(--acct-success);
  color: #fff;
  border-color: var(--acct-success);
}
.ca-acct-action.on-danger {
  background: var(--acct-danger-soft);
  border-color: rgba(220, 38, 38, 0.3);
  color: var(--acct-danger);
}
.ca-acct-action.on-danger:hover {
  background: var(--acct-danger);
  color: #fff;
  border-color: var(--acct-danger);
}
.dark-layout .ca-acct-action.on-success { background: rgba(34, 197, 94, 0.18); color: #6dd29a; }
.dark-layout .ca-acct-action.on-danger { background: rgba(239, 68, 68, 0.18); color: #ff8a95; }

.ca-acct-action-delete {
  color: var(--acct-danger);
  border-color: rgba(220, 38, 38, 0.35);
}
.ca-acct-action-delete:hover {
  background: var(--acct-danger);
  border-color: var(--acct-danger);
  color: #fff;
}
.dark-layout .ca-acct-action-delete { color: #ff8a95; border-color: rgba(239, 68, 68, 0.4); }
.dark-layout .ca-acct-action-delete:hover { background: var(--acct-danger); color: #fff; }

/* New-trade: primary/accent so it stands out as the main action */
.ca-acct-action-trade {
  background: var(--acct-primary);
  border-color: var(--acct-primary);
  color: #fff;
}
.ca-acct-action-trade:hover {
  background: var(--acct-primary);
  border-color: var(--acct-primary);
  color: #fff;
  filter: brightness(1.08);
}

/* ===== STATS ===== */
.ca-acct-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}

.ca-acct-stat {
  background: var(--acct-bg);
  border: 1px solid var(--acct-border);
  border-radius: 12px;
  padding: 12px 14px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.ca-acct-stat:hover {
  border-color: var(--acct-border-strong);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.ca-acct-stat-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--acct-text-muted);
  margin-bottom: 4px;
}

.ca-acct-stat-icon { font-size: 0.78rem; }
.ca-acct-icon-warn { color: var(--acct-warn); }
.ca-acct-icon-info { color: var(--acct-info); }

.ca-acct-stat-value {
  font-size: 1.05rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--acct-text);
  line-height: 1.25;
}

.ca-acct-stat-text {
  font-size: 0.95rem;
  color: var(--acct-text);
}

.ca-acct-stat-sub {
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--acct-text-muted);
  margin-left: 4px;
}

.ca-acct-margin-call {
  display: inline-block;
  margin-left: 6px;
  background: var(--acct-danger);
  color: #fff;
  font-size: 0.62rem;
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 999px;
  letter-spacing: 0.6px;
  animation: ca-acct-pulse 1.6s ease-in-out infinite;
}

@keyframes ca-acct-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.5); }
  50%      { box-shadow: 0 0 0 5px rgba(220, 38, 38, 0); }
}

/* ===== EMPTY ===== */
.ca-acct-empty {
  text-align: center;
  padding: 48px 20px;
  background: var(--acct-surface);
  border: 1px dashed var(--acct-border-strong);
  border-radius: 14px;
}
.ca-acct-empty-icon {
  display: inline-flex;
  width: 72px; height: 72px;
  border-radius: 50%;
  background: var(--acct-primary-soft);
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  font-size: 1.7rem;
  color: var(--acct-primary);
}
.ca-acct-empty-text {
  font-size: 0.95rem;
  color: var(--acct-text-2);
  font-weight: 500;
}
</style>
